import React from 'react';
import { focusAsync, getFirstTabbable } from '@fluentui/react';

// Visually hides elements which remain visible for screen reader
export const screenReaderOnlyStyles: React.CSSProperties = {
  border: '0',
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: '0',
  position: 'absolute',
  width: '1px',

  // Some screen readers (e.g., Narrator) will incorrectly
  // read text with these visually-hidden styles + certain overflow rules
  // as single characters rather than complete words.
  // See: https://microsoft.visualstudio.com/OS/_workitems/edit/41767491
  overflowWrap: 'normal',
  textOverflow: 'initial',
};

// Styles for an aria-label overlaying the text region to be visually hidden but shown for screen readers
export const textAriaLabelOverlayStyles: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  opacity: 0,
  overflow: 'hidden',
  textWrap: 'nowrap',
  textOverflow: 'clip',
  overflowWrap: 'normal',
};

// Styles for the wrapper containing the text value and the aria-label overlay with textAriaLabelOverlayStyles applied
export const textAriaLabelOverlayWrapperStyles: React.CSSProperties = {
  position: 'relative',
  overflowX: 'hidden',
};

/**
 * Finds the first invalid input element in wrapper & moves focus to it.
 * Attribute 'data-identity-invalid-field' is used to mark invalid fields
 * because we cannot rely on `aria-invalid` due to ComboBox structure.
 * Using `aria-invalid` in on ComboBox would apply that attribute to
 * a div & make the ARIA usage itself invalid.
 *
 * @param wrapperId {string} The id of the wrapper element that contains the inputs.
 */
export const focusInvalidInput = (wrapperId: string) => {
  const firstError = document.querySelector(`#${wrapperId} [data-identity-invalid-field="true"]`) as HTMLElement;
  if (!firstError) {
    return;
  }
  // Input fields and controls that expose the invalid-field marker on their focus target:
  if (firstError.tagName === 'INPUT' || (firstError.hasAttribute('tabindex') && firstError.tabIndex >= 0)) {
    firstError.focus();
    return;
  }
  // Composite controls such as ComboBox and Dropdown:
  firstError.querySelector<HTMLElement>('input, [tabindex]:not([tabindex="-1"])')?.focus();
};

/**
 * Given a wrapper id, sets focus asynchronously
 * to the first tabbable descendent of the wrapper element.
 *
 * @param id {string} Valid id of the wrapper element
 */
export const focusFirstTabbable = (id: string) => {
  const wrapperElem: HTMLElement | null = document.getElementById(id);
  if (wrapperElem) {
    focusAsync(getFirstTabbable(wrapperElem, wrapperElem, true));
  }
};

/**
 * A visually hidden period that will be picked up by screenreaders only.
 * Used in cases where we want to signal to a screenreader to pause
 * between 2 different strings.
 */
export const screenReaderPause = <span style={screenReaderOnlyStyles}>.{` `}</span>;

/**
 * Selector to check if high contrast mode is NOT active
 *
 * Note: "As of Microsoft Edge, -ms-high-contrast: none is no longer supported."
 * (see: https://learn.microsoft.com/en-us/previous-versions/hh771830(v=vs.85)#possible-values)
 * So we are negating the first part of the query with 'not' instead.
 */
export const notHighContrastSelector = '@media not screen and (-ms-high-contrast: active), screen and (forced-colors: none)';
